import type {
  DrpSource,
  DrpFormat,
  DrpChecklistRow,
  EducationChecklistRow,
  TextConsultationContext,
} from "common/models/types";
import type {
  CreateTextConsultationBody,
  UpdateTextConsultationBody,
  CreateTextConsultationData,
} from "api/consultationService";
import { guidanceOf } from "utils/checklistGuidance";
import { buildCreateBody, buildUpdateBody } from "./buildTextRecordBody";

/**
 * 모듈 수준 직렬화 저장 큐(spec 설계 결정 7).
 *
 * 슬라이드가 unmount된 뒤에도 요청을 이어 보낸다 — 최신 상태·진행 플래그·대기 변경을 모듈에 둔다.
 * 한 번에 하나만 보낸다. 진행 중 생긴 변경은 모아 두었다가 응답 뒤 한 번에 보낸다(마지막 상태가 이긴다).
 * 첫 생성 응답 전의 변경은 그 POST 응답을 기다린 뒤 받은 id로 PATCH 1회(id 없는 두 번째 POST 없음).
 */

export type SaveStatus = "idle" | "saving" | "saved" | "failed" | "empty";

export interface TextRecordPayload {
  patientId: number;
  reviewId: number | null;
  drpSource: DrpSource;
  drpFormat: DrpFormat;
  drpItems: DrpChecklistRow[];
  educationItems: EducationChecklistRow[];
  note: string;
  consultedAt: string;
}

interface ApiResult<T> {
  error: number;
  data?: T;
  message?: string;
  status?: number;
}

/** Page가 넘기는 영속 콜백(unmount 뒤에도 유지). */
export interface PersistentCallbacks {
  onCreate: (body: CreateTextConsultationBody) => Promise<ApiResult<CreateTextConsultationData>>;
  onUpdate: (id: number, body: UpdateTextConsultationBody) => Promise<ApiResult<void>>;
  /** 첫 생성 성공 때 1회(재조회 + 성공 토스트). */
  onCreated?: (recordId: number) => void;
}

/** 컴포넌트가 넘기는 UI 콜백(unmount 때 비운다). */
export interface UiCallbacks {
  onStatus?: (status: SaveStatus) => void;
  onError?: (kind: "400" | "404" | "network", message: string) => void;
  /** 다른 탭이 만든 기록(첫 POST가 existing) — 저장본을 화면에 병합하고 수정 모드로. */
  onExisting?: (savedContext: TextConsultationContext, recordId: number) => void;
}

interface QueueEntry {
  key: string;
  recordId: number | null;
  latest: TextRecordPayload | null;
  inFlight: boolean;
  pending: boolean;
  postSendCount: number;
  firstCreateDone: boolean;
  /** 마지막으로 성공 저장한 payload 서명(dirty 판정). */
  lastSavedSig: string | null;
  /** 400으로 거절된 payload 서명(같은 내용 재전송 억제). */
  lastRejectedSig: string | null;
  toast400Shown: boolean;
  hasFailedPending: boolean;
  persistent: PersistentCallbacks;
  ui: UiCallbacks;
  flushWaiters: Array<() => void>;
}

const entries = new Map<string, QueueEntry>();

const MSG_400 = "저장할 수 없는 내용이 있어요. 관리자에게 문의해 주세요.";
const MSG_404_CREATE = "환자를 찾을 수 없어요.";
const MSG_404_UPDATE = "기록을 찾을 수 없어요.";
const MSG_NETWORK = "저장하지 못했어요.";

/**
 * 빈 기록 판정 한 곳(설계 결정 21) — "O·X가 하나도 없고 note가 trim 후 빈 문자열".
 * 호출부 셋(첫 POST 차단·이탈 확인·머리 상태 "비어 있음")이 모두 이 식을 쓴다. X만 있어도 저장된다.
 */
export function isEmptyPayload(p: TextRecordPayload): boolean {
  const anyMarked =
    p.drpItems.some((r) => guidanceOf(r) !== undefined) ||
    p.educationItems.some((r) => guidanceOf(r) !== undefined);
  return !anyMarked && p.note.trim().length === 0;
}

function sigOf(p: TextRecordPayload): string {
  return JSON.stringify({
    reviewId: p.reviewId,
    drpSource: p.drpSource,
    drpFormat: p.drpFormat,
    drpItems: p.drpItems,
    educationItems: p.educationItems,
    note: p.note,
    consultedAt: p.consultedAt,
  });
}

/** 큐 엔트리를 보장하고 콜백을 갱신한다(mount 시). */
export function ensureEntry(
  key: string,
  recordId: number | null,
  persistent: PersistentCallbacks,
  ui: UiCallbacks,
): void {
  const existing = entries.get(key);
  if (existing) {
    existing.persistent = persistent;
    existing.ui = ui;
    if (existing.recordId == null && recordId != null) existing.recordId = recordId;
    return;
  }
  entries.set(key, {
    key,
    recordId,
    latest: null,
    inFlight: false,
    pending: false,
    postSendCount: 0,
    firstCreateDone: false,
    lastSavedSig: null,
    lastRejectedSig: null,
    toast400Shown: false,
    hasFailedPending: false,
    persistent,
    ui,
    flushWaiters: [],
  });
}

/** unmount 때 UI 콜백만 비운다(API·onCreated는 유지돼 요청이 이어진다). */
export function clearUiCallbacks(key: string): void {
  const e = entries.get(key);
  if (e) e.ui = {};
}

export function getRecordId(key: string): number | null {
  return entries.get(key)?.recordId ?? null;
}

/** 대기 변경(저장 안 된 변경 또는 실패한 저장)이 있는가 — 이탈 확인창·닫기 확인용. */
export function hasUnsavedChange(key: string): boolean {
  const e = entries.get(key);
  if (!e) return false;
  if (e.inFlight || e.pending) return true;
  if (e.hasFailedPending) return true;
  if (e.latest && sigOf(e.latest) !== e.lastSavedSig) {
    // 저장된 적 없는데 비어 있으면 대기 없음(아무것도 안 보냄).
    if (e.recordId == null && isEmptyPayload(e.latest)) return false;
    return true;
  }
  return false;
}

/** 저장 큐가 빌 때까지 기다린다(상세 로더·재열기용). */
export function flush(key: string): Promise<void> {
  const e = entries.get(key);
  if (!e || (!e.inFlight && !e.pending)) return Promise.resolve();
  return new Promise<void>((resolve) => e.flushWaiters.push(resolve));
}

/**
 * 진행 중·대기 중인 모든 큐가 빌 때까지 기다린다.
 * 읽기 화면 재열기는 열 때마다 key가 달라(설계 결정 7 고유 key) 직전 슬라이드의 저장 큐를
 * key로 특정하기 어렵다 — 텍스트 슬라이드는 한 번에 하나뿐이므로 전체를 기다려 옛 내용 경쟁을 막는다.
 */
export function flushAll(): Promise<void> {
  const waits: Array<Promise<void>> = [];
  entries.forEach((e) => {
    if (e.inFlight || e.pending) {
      waits.push(new Promise<void>((resolve) => e.flushWaiters.push(resolve)));
    }
  });
  return Promise.all(waits).then(() => undefined);
}

function resolveWaiters(e: QueueEntry): void {
  const waiters = e.flushWaiters;
  e.flushWaiters = [];
  waiters.forEach((w) => w());
}

/**
 * 저장을 큐에 넣는다. 트리거(체크·blur·3초·unmount flush·가져오기)가 부른다.
 */
export function enqueueSave(key: string, payload: TextRecordPayload): void {
  const e = entries.get(key);
  if (!e) return;
  e.latest = payload;

  // 저장된 적 없고 비어 있으면 보내지 않는다(기록이 생기지 않음).
  if (e.recordId == null && isEmptyPayload(payload)) {
    e.ui.onStatus?.("idle");
    return;
  }

  // 400으로 거절된 내용과 같으면 재전송 억제(내용이 바뀌면 서명이 달라져 통과).
  if (e.lastRejectedSig && sigOf(payload) === e.lastRejectedSig) {
    return;
  }

  if (e.inFlight) {
    e.pending = true;
    return;
  }
  void runSave(key);
}

async function runSave(key: string): Promise<void> {
  const e = entries.get(key);
  if (!e || !e.latest) return;

  e.inFlight = true;
  e.pending = false;
  const payload = e.latest;
  e.ui.onStatus?.("saving");

  try {
    if (e.recordId == null) {
      e.postSendCount += 1;
      const res = await e.persistent.onCreate(buildCreateBody(payload));
      if (res.error === 0 && res.data) {
        const id = res.data.id;
        e.recordId = id;
        if (res.data.existing) {
          if (e.postSendCount > 1) {
            // 앞선 POST의 응답이 유실됐다 — 저장본은 이 슬라이드가 만든 것이므로 현재 상태로 PATCH 1회(갈림길 표).
            e.pending = true;
          } else if (!e.ui.onExisting) {
            // 슬라이드가 이미 닫혔다 — 병합할 화면이 없으니 PATCH를 보내지 않고 저장본을 보존한다(설계 결정 21 결함 ①).
            e.pending = false;
            e.latest = null;
          } else if (res.data.context) {
            // 다른 탭이 만든 기록 — 화면이 저장본을 병합한 뒤 병합본을 enqueueSave로 다시 넣는다(PATCH 1회, 결함 ①).
            e.ui.onExisting(res.data.context, id);
          } else {
            // 저장본이 없으면 병합할 수 없어 현재 상태로 PATCH 1회.
            e.pending = true;
          }
        } else {
          if (!e.firstCreateDone) {
            e.firstCreateDone = true;
            e.persistent.onCreated?.(id);
          }
        }
        markSaved(e, payload);
      } else {
        handleError(e, res, true);
      }
    } else {
      const res = await e.persistent.onUpdate(e.recordId, buildUpdateBody(payload));
      if (res.error === 0) {
        markSaved(e, payload);
      } else {
        handleError(e, res, false);
      }
    }
  } catch (err) {
    e.hasFailedPending = true;
    e.ui.onStatus?.("failed");
    e.ui.onError?.("network", MSG_NETWORK);
  } finally {
    e.inFlight = false;
    if (e.pending && !isSuppressedNext(e)) {
      e.pending = false;
      void runSave(key);
    } else {
      e.pending = false;
      resolveWaiters(e);
    }
  }
}

function isSuppressedNext(e: QueueEntry): boolean {
  if (!e.latest) return true;
  if (e.lastRejectedSig && sigOf(e.latest) === e.lastRejectedSig) return true;
  return false;
}

function markSaved(e: QueueEntry, payload: TextRecordPayload): void {
  e.lastSavedSig = sigOf(payload);
  e.lastRejectedSig = null;
  e.hasFailedPending = false;
  e.ui.onStatus?.(isEmptyPayload(payload) ? "empty" : "saved");
}

function handleError(e: QueueEntry, res: ApiResult<unknown>, isCreate: boolean): void {
  const status = res.status;
  if (status === 400) {
    e.lastRejectedSig = e.latest ? sigOf(e.latest) : null;
    e.hasFailedPending = true;
    e.ui.onStatus?.("failed");
    if (!e.toast400Shown) {
      e.toast400Shown = true;
      e.ui.onError?.("400", MSG_400);
    }
  } else if (status === 404) {
    // 생성 404 = 환자 없음("환자를 찾을 수 없어요"), 수정 404 = 기록이 지워짐("기록을 찾을 수 없어요").
    // 재시도·대기 변경으로 치지 않는다 — 화면이 토스트 후 닫는다(결함 3, 설계 결정 18).
    e.ui.onError?.("404", isCreate ? MSG_404_CREATE : MSG_404_UPDATE);
  } else {
    // 네트워크·5xx — 입력 유지, 다음 트리거·「다시 시도」에서 재전송.
    e.hasFailedPending = true;
    e.ui.onStatus?.("failed");
    e.ui.onError?.("network", MSG_NETWORK);
  }
}

/** 테스트 격리용 — 모든 엔트리 제거. */
export function __resetQueueForTest(): void {
  entries.clear();
}

// ── 열림 레지스트리(입구 버튼 재클릭 무시, 설계 결정 7) ────────────────────
// 같은 환자·보고서·모드로 이미 열려 있으면 재클릭을 무시한다. hide 뒤 500ms 동안 등록이 남는다.
const openEnvs = new Map<string, number>();

export function textRecordEnvKey(patientId: number, reviewId: number | null, mode: string): string {
  return `${patientId}:${reviewId ?? "none"}:${mode}`;
}

export function isTextRecordOpen(env: string): boolean {
  return (openEnvs.get(env) ?? 0) > 0;
}

export function markTextRecordOpen(env: string): void {
  openEnvs.set(env, (openEnvs.get(env) ?? 0) + 1);
}

export function markTextRecordClosing(env: string): void {
  // hide 뒤 500ms 동안 등록을 남겨, 그 사이 재클릭을 무시한다(수용).
  window.setTimeout(() => {
    const n = (openEnvs.get(env) ?? 1) - 1;
    if (n <= 0) openEnvs.delete(env);
    else openEnvs.set(env, n);
  }, 500);
}
